import { loadConfig, resolveLanguage, setDisplayLanguage, translate, useLanguage } from "@cqt/platform";
/**
 * Public signup page — reached from a one-time invite link.
 *
 * Rendered OUTSIDE AuthProvider (see main.tsx). It must not be: AuthProvider
 * forces a Keycloak login, and the whole point of this page is that the person
 * does not have an account yet.
 *
 * On the dark brand plate in both themes, like the login splash — it is the same
 * surface the logo is drawn for, and it is the first thing a new hire ever sees
 * of the platform.
 */
import { useEffect, useState } from "react";
import { Logo, ThemeToggle } from "@cqt/brand";

interface SignupInfo {
  email: string;
  full_name: string | null;
  company_name: string | null;
}

type Phase =
  | { k: "loading" }
  | { k: "invalid"; detail: string }
  | { k: "form"; info: SignupInfo }
  | { k: "saving"; info: SignupInfo }
  | { k: "done"; email: string; loginUrl: string };

const MIN_LENGTH = 12;

function Plate({ children }: { children: React.ReactNode }) {
  useLanguage();
  return (
    <div
      className="plate"
      style={{ minHeight: "100dvh", display: "grid", placeItems: "center", padding: 24 }}
    >
      <div style={{ position: "fixed", top: 16, right: 16 }}>
        <ThemeToggle onPlate />
      </div>
      <div style={{ width: "min(420px, 100%)", display: "grid", justifyItems: "center", gap: 22 }}>
        {/* Neutral initials until a company is known. Signup is reached
            from a link, so there may be no resolved branding yet. */}
        <Logo size={132} />
        {children}
      </div>
    </div>
  );
}

export function Signup() {
  useLanguage();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const [phase, setPhase] = useState<Phase>({ k: "loading" });
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadConfig().then(config => setDisplayLanguage(resolveLanguage(config.language))).catch(() => {});
    if (!token) {
      setPhase({ k: "invalid", detail: "This link is missing its token." });
      return;
    }
    // A plain fetch, not the authed helper: there is no token to send, and using
    // the authed helper here would be the one place it is wrong.
    fetch(`/api/signup/${encodeURIComponent(token)}`, { cache: "no-store" })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.detail || "This invite link is not valid.");
        return body as SignupInfo;
      })
      .then((info) => {
        setPhase({ k: "form", info });
        if (info.full_name) setFullName(info.full_name);
      })
      .catch((err) => setPhase({ k: "invalid", detail: err.message }));
  }, [token]);

  const submit = async () => {
    if (phase.k !== "form") return;
    setError(null);

    if (password.length < MIN_LENGTH) {
      setError(translate("Use at least {count} characters. A short phrase you can remember beats a short scramble.", {count:MIN_LENGTH}));
      return;
    }
    if (password !== confirm) {
      setError(translate("The two passwords do not match."));
      return;
    }

    const info = phase.info;
    setPhase({ k: "saving", info });
    try {
      const res = await fetch(`/api/signup/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, full_name: fullName.trim() || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Your account could not be created.");
      setPhase({ k: "done", email: body.email, loginUrl: body.login_url || "/" });
    } catch (err) {
      // Back to the form so they can retry — but the invite may now be spent, so
      // the message from the server is shown verbatim rather than softened.
      setPhase({ k: "form", info });
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (phase.k === "loading") {
    return (
      <Plate>
        <div className="spinner" aria-hidden="true" />
        <p className="meta" style={{ letterSpacing: "0.2em", textTransform: "uppercase" }}>
           {translate("Checking your invite")} </p>
      </Plate>
    );
  }

  if (phase.k === "invalid") {
    return (
      <Plate>
        <h1 style={{ color: "var(--cqt-bone)", fontSize: "1.35rem", textAlign: "center" }}>
           {translate("This invite is not usable")} </h1>
        <p style={{ color: "var(--cqt-ash-2)", fontSize: "0.92rem", lineHeight: 1.55, textAlign: "center" }}>
          {translate(phase.detail)}
        </p>
        <p style={{ color: "var(--cqt-ash)", fontSize: "0.85rem", lineHeight: 1.5, textAlign: "center" }}>
           {translate("Invite links can only be used once and expire after a few days. Ask whoever sent it for a fresh one.")} </p>
      </Plate>
    );
  }

  if (phase.k === "done") {
    return (
      <Plate>
        <h1 style={{ color: "var(--cqt-bone)", fontSize: "1.35rem", textAlign: "center" }}>
           {translate("You’re all set")} </h1>
        <p style={{ color: "var(--cqt-ash-2)", fontSize: "0.92rem", lineHeight: 1.55, textAlign: "center" }}>
           {translate("Your account is ready. Sign in with")} <strong style={{ color: "var(--cqt-bone)" }}>{phase.email}</strong>{" "}
           {translate("and the password you just chose.")} </p>
        <a className="btn btn--primary" href={phase.loginUrl} style={{ width: "100%" }}>
           {translate("Go to the platform")} </a>
      </Plate>
    );
  }

  const busy = phase.k === "saving";
  const info = phase.info;

  return (
    <Plate>
      <div style={{ textAlign: "center" }}>
        <div className="eyebrow" style={{ color: "var(--cqt-brass)" }}>
          {info.company_name ?? "CQT Ammo"}
        </div>
        <h1 style={{ color: "var(--cqt-bone)", fontSize: "1.5rem", marginTop: 4 }}>
           {translate("Set up your account")} </h1>
      </div>

      <form
        style={{ width: "100%" }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="field" style={{ marginBottom: 14 }}>
          <label className="label" style={{ color: "var(--cqt-ash-2)" }} htmlFor="su-email">
             {translate("Email")} </label>
          {/* Read-only: the invite was issued to this address, and letting it be
              edited would let one link create an account for anyone. */}
          <input
            id="su-email"
            className="input mono"
            value={info.email}
            readOnly
            aria-describedby="su-email-hint"
          />
          <span id="su-email-hint" className="hint">
             {translate("This invite was issued to this address.")} </span>
        </div>

        <div className="field" style={{ marginBottom: 14 }}>
          <label className="label" style={{ color: "var(--cqt-ash-2)" }} htmlFor="su-name">
             {translate("Your name")} </label>
          <input
            id="su-name"
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
            disabled={busy}
          />
        </div>

        <div className="field" style={{ marginBottom: 14 }}>
          <label className="label" style={{ color: "var(--cqt-ash-2)" }} htmlFor="su-pw">
             {translate("Choose a password")} </label>
          <input
            id="su-pw"
            className="input"
            type={reveal ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            disabled={busy}
            aria-describedby="su-pw-hint"
          />
          <span id="su-pw-hint" className="hint">
             {translate("At least")} {MIN_LENGTH}  {translate("characters. Nobody else ever sees it, including whoever invited you.")} </span>
        </div>

        <div className="field" style={{ marginBottom: 10 }}>
          <label className="label" style={{ color: "var(--cqt-ash-2)" }} htmlFor="su-pw2">
             {translate("Confirm password")} </label>
          <input
            id="su-pw2"
            className="input"
            type={reveal ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            disabled={busy}
          />
        </div>

        <label className="row" style={{ gap: 8, marginBottom: 16, color: "var(--cqt-ash-2)", fontSize: "0.85rem" }}>
          <input type="checkbox" checked={reveal} onChange={(e) => setReveal(e.target.checked)} />
           {translate("Show password")} </label>

        {error ? (
          <p className="error-text" role="alert" style={{ marginBottom: 12 }}>
            {translate(error)}
          </p>
        ) : null}

        <button
          className="btn btn--primary"
          type="submit"
          style={{ width: "100%" }}
          disabled={busy || !password || !confirm}
        >
          {busy ? translate("Creating your account…") : translate("Create my account")}
        </button>
      </form>
    </Plate>
  );
}
